import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface ComboboxOption {
  value: string
  label: string
  description?: string
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  onSearch?: (searchTerm: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  loading?: boolean
  loadingMessage?: string | React.ReactNode
  disabled?: boolean
  className?: string
  maxHeight?: string
  align?: "start" | "center" | "end"
  side?: "top" | "right" | "bottom" | "left"
  sideOffset?: number
  avoidCollisions?: boolean
  /** Padding from viewport edges when resolving collisions (px). */
  collisionPadding?: number
}

const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  ({
    options = [],
    value,
    onValueChange,
    onSearch,
    placeholder,
    searchPlaceholder,
    emptyMessage,
    loading = false,
    loadingMessage,
    disabled = false,
    className,
    maxHeight = "200px",
    align = "start",
    side = "bottom",
    sideOffset = 4,
    avoidCollisions = true,
    collisionPadding = 12,
    ...props
  }, ref) => {
    const [open, setOpen] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState("")

    const selectedOption = options.find((option) => option.value === value)

    const handleSearch = (searchValue: string) => {
      setSearchTerm(searchValue)
      if (onSearch) {
        onSearch(searchValue)
      }
    }

    const handleSelect = (selectedValue: string) => {
      if (onValueChange) {
        onValueChange(selectedValue)
      }
      setSearchTerm("")
      setOpen(false)
    }

    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between", className)}
            disabled={disabled || loading}
            {...props}
          >
            {loading
              ? loadingMessage
              : selectedOption
                ? selectedOption.label
                : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align={align}
          side={side}
          sideOffset={sideOffset}
          avoidCollisions={avoidCollisions}
          collisionPadding={collisionPadding}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="w-full">
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchTerm}
              onValueChange={handleSearch}
              className="w-full"
            />
            <div 
              className={cn("overflow-y-auto overscroll-contain")}
              style={{ maxHeight }}
              onWheel={(e) => {
                e.stopPropagation()
                const element = e.currentTarget
                element.scrollTop += e.deltaY
              }}
            >
              <CommandList className="w-full overflow-visible">
                <CommandEmpty className="w-full text-sm p-2 text-white/40">
                  {loading ? loadingMessage : emptyMessage}
                </CommandEmpty>
                {filteredOptions.length > 0 && (
                  <CommandGroup className="w-full">
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={`${option.value} ${option.label} ${option.description || ""}`}
                        onSelect={() => handleSelect(option.value)}
                        className="w-full"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col w-full min-w-0">
                          <span className="font-medium truncate">{option.label}</span>
                          {option.description && (
                            <span className="text-sm text-muted-foreground truncate">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)

Combobox.displayName = "Combobox"

export { Combobox }
export type { ComboboxOption }
